// Household cluster merge overlay — Baker: "I lost the ability to
// select which fields to keep when merging" + "lost the ability to
// choose which one I get to merge into" — both regressions surfaced
// when the mortgage import placeholder-household merge detector started
// emitting HOUSEHOLD-level merge issues.
//
// ONE screen (Grant 2026-07-10): the per-field review opens immediately
// with the recommended surviving household pre-selected, and a one-click
// "swap" flips which household survives. The old flow made you choose the
// survivor on a separate step BEFORE you could see any of the fields —
// "I don't know which household survives until I've reviewed every field."
// The recommended survivor comes from the detector (survivor_id on the
// recommendation, threaded in as defaultSurvivorId); when absent we fall
// back to refs[1] (the emit convention is [placeholder, real], so index 1
// is the household that should absorb the other).
//
// The merge call goes through the proven reconciliationApi.mergeHouseholds
// path (the same call BulkHouseholdMergeModal uses) so the substrate stays
// exactly one and the audit/version write happens. The parent
// (CleanupWorkspacePage / CleanupProvider) then marks the cleanup issue
// resolved (same pattern as the contact orchestrator's `onMerged`).

import { useEffect, useState } from "react";

import {
  api,
  ApiError,
  type Household,
  type MemberMergeRequest,
  reconciliationApi,
} from "../api";
import { HouseholdMergeReview } from "../components/HouseholdMergeReview";
import {
  buildContactNameMap,
  buildMemberOptions,
  describeFailedMemberMerges,
  type MergeMemberOption,
} from "../components/household-merge-review-helpers";
import { Modal } from "../components/Modal";
import { useToast } from "../components/Toast";
import { Button, ErrorBanner, LoadingBlock } from "../ui";
import { displayLabel, type RecordRef } from "./cleanup-records";
import { mergedAwayFromError, type MergedAwayInfo } from "./merged-away";

type Step = "loading" | "review" | "error";

export function HouseholdClusterMergeOverlay({
  refs,
  onClose,
  onMerged,
  defaultSurvivorId,
  initialReason,
  samePersonConfirmed,
}: {
  refs: RecordRef[];
  onClose: () => void;
  onMerged: () => void;
  // The recommended surviving household. The mortgage placeholder-merge
  // path passes the detector's survivor_id (the real household); the
  // "link a couple as one household" path passes the bigger household it
  // computed. Optional — the review defaults to refs[1] when absent.
  defaultSurvivorId?: string;
  initialReason?: string;
  // The person already answered "same person under two surnames"; the
  // review then offers (ticked) to combine the two person cards too.
  samePersonConfirmed?: boolean;
}) {
  const [households, setHouseholds] = useState<Household[]>([]);
  // contact-id → name, built from every fetched household's members so
  // the review's Primary contact row shows names, not UUIDs.
  const [contactNameById, setContactNameById] = useState<
    Record<string, string>
  >({});
  // Each household's people, keyed by household id so the review can
  // offer to fold two cards for one person together (Grant, 2026-09-17).
  const [membersByHouseholdId, setMembersByHouseholdId] = useState<
    Record<string, MergeMemberOption[]>
  >({});
  const toast = useToast();
  const [survivorId, setSurvivorId] = useState<string>("");
  const [step, setStep] = useState<Step>("loading");
  const [error, setError] = useState<string | null>(null);
  // Set when a load fails because a household was already merged away —
  // carries the surviving household so the error offers a way forward
  // instead of dead-ending (Grant 2026-07-16).
  const [mergedAway, setMergedAway] = useState<MergedAwayInfo | null>(null);

  // Fetch both households so HouseholdMergeReview gets full Household
  // objects (not refs) and the field comparison can render immediately.
  useEffect(() => {
    let cancelled = false;
    setStep("loading");
    setError(null);
    setMergedAway(null);
    void (async () => {
      try {
        const fetched = await Promise.all(
          refs.map((r) => api.getHousehold(r.id)),
        );
        if (cancelled) return;
        const hhs = fetched.map((d) => d.household);
        setHouseholds(hhs);
        setContactNameById(buildContactNameMap(...fetched.map((d) => d.contacts)));
        setMembersByHouseholdId(
          Object.fromEntries(
            fetched.map((d) => [
              d.household.id,
              buildMemberOptions(d.contacts),
            ]),
          ),
        );
        // Default survivor = the detector's recommendation when supplied
        // (survivor_id, or the couple-link preselect), else refs[1] — the
        // affectedRecords emit convention is [placeholder, real], so the
        // second record is the one that should survive. The user flips it
        // with the swap control in the review; no reliance on label text.
        const explicit =
          defaultSurvivorId && hhs.some((h) => h.id === defaultSurvivorId)
            ? defaultSurvivorId
            : null;
        setSurvivorId(explicit ?? hhs[1]?.id ?? hhs[0]?.id ?? "");
        setStep("review");
      } catch (e) {
        if (cancelled) return;
        const merged = mergedAwayFromError(e);
        setMergedAway(merged);
        setError(
          merged
            ? merged.message
            : e instanceof ApiError
              ? e.message
              : "Couldn't load both households to review.",
        );
        setStep("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refs, defaultSurvivorId]);

  const survivor = households.find((h) => h.id === survivorId) ?? null;
  const merged = households.find((h) => h.id !== survivorId) ?? null;

  // Throws on failure so HouseholdMergeReview surfaces the error inline (it
  // owns the confirm modal + error display; the parent owns the network
  // call so error handling stays consistent across surfaces).
  async function submit(payload: {
    field_overrides: Record<string, unknown>;
    reason: string;
    member_merges: MemberMergeRequest[];
  }) {
    if (!survivor || !merged) return;
    const res = await reconciliationApi.mergeHouseholds(survivor.id, {
      merged_household_id: merged.id,
      reason: payload.reason,
      field_overrides: payload.field_overrides as Record<
        string,
        "primary" | "merged"
      >,
      ...(payload.member_merges.length > 0
        ? { member_merges: payload.member_merges }
        : {}),
    });
    if (!res.success) {
      throw new Error(
        `Merge of "${displayLabel(merged.display_name)}" into "${displayLabel(
          survivor.display_name,
        )}" did not complete.`,
      );
    }
    // The households merged either way, so a pair that could not be folded
    // together has to be said out loud rather than passing as done.
    const outcomes = res.member_merge_outcomes ?? [];
    const folded = outcomes.filter((o) => o.merged).length;
    if (folded > 0) {
      toast.success(
        folded === 1
          ? "1 duplicate person was folded into a single record"
          : `${folded} duplicate people were folded into single records`,
      );
    }
    for (const line of describeFailedMemberMerges(outcomes, contactNameById)) {
      toast.warning(line);
    }
    onMerged();
  }

  // Flip which household survives. With exactly two households the "other"
  // one becomes the survivor; HouseholdMergeReview re-derives its per-field
  // defaults from the new survivor/merged pair.
  function swapSurvivor() {
    if (merged) setSurvivorId(merged.id);
  }

  let body: React.ReactNode;
  let title: string;
  if (step === "loading") {
    title = "Loading households…";
    body = <LoadingBlock>Loading both households…</LoadingBlock>;
  } else if (step === "error") {
    title = mergedAway ? "This household was already merged" : "Couldn't load this merge";
    body = (
      <>
        <ErrorBanner message={error ?? "Unknown error"} />
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            marginTop: 12,
          }}
        >
          <Button variant="sw-default" onClick={onClose}>
            Close
          </Button>
          {mergedAway && (
            <a
              href={mergedAway.href}
              data-testid="merge-open-survivor"
              style={{
                display: "inline-flex",
                alignItems: "center",
                background: "var(--blue)",
                color: "var(--white)",
                textDecoration: "none",
                padding: "6px 14px",
                borderRadius: 6,
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              Open the surviving household
            </a>
          )}
        </div>
      </>
    );
  } else {
    // step === "review"
    title = "Review the fields, then merge";
    body =
      survivor && merged ? (
        <HouseholdMergeReview
          survivor={survivor}
          merged={merged}
          contactNameById={contactNameById}
          membersByHouseholdId={membersByHouseholdId}
          samePersonConfirmed={samePersonConfirmed === true}
          onSwapSurvivor={households.length === 2 ? swapSurvivor : undefined}
          initialReason={
            initialReason ?? "Merged a duplicate household during cleanup."
          }
          onSubmit={submit}
          onCancel={onClose}
        />
      ) : (
        <ErrorBanner message="Lost track of which household survives — close and reopen." />
      );
  }

  return (
    // modal-wide (1100px) so the per-field survivor/merged table isn't
    // truncated the way the default 520px modal truncated it (Baker,
    // 2026-07-14). Tall content scrolls inside .modal-body.
    <Modal
      open={true}
      onClose={onClose}
      title={title}
      {...(step === "review" ? { className: "modal-wide" } : {})}
    >
      {body}
    </Modal>
  );
}
